import React, { useEffect, useState, Suspense } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FiChevronRight, FiAlertCircle, FiArrowRight, FiLoader } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getToolBySlug, getToolsByCategory } from '../data/registry';
import { SEO } from '../components/layout/SEO';

export const ToolPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [tool, setTool] = useState(null);
  const [relatedTools, setRelatedTools] = useState([]);

  useEffect(() => {
    const foundTool = getToolBySlug(slug);
    if (!foundTool) {
      navigate('/', { replace: true });
      return;
    }
    
    setTool(foundTool);
    
    // Get up to 4 other tools from same category
    const related = getToolsByCategory(foundTool.category)
      .filter(t => t.slug !== slug)
      .slice(0, 4);
    
    setRelatedTools(related);
    
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, [slug, navigate]);

  if (!tool) return null;

  const ToolComponent = tool.component;

  // SEO Data
  const seoTitle = `${tool.name} — Free Online ${tool.category} Tool | FreeToolSuitePro`;
  const seoUrl = `https://freetoolsuitepro.com/tools/${tool.slug}`;
  const webAppSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": tool.name,
    "description": tool.description,
    "applicationCategory": "BrowserApplication",
    "operatingSystem": "All",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };

  return (
    <div className="w-full bg-slate-50 dark:bg-slate-950 min-h-screen pb-20">
      <SEO 
        title={seoTitle} 
        description={tool.description} 
        url={seoUrl} 
        schema={webAppSchema}
        faqs={tool.faqs}
      />

      {/* Breadcrumb & Header */}
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 pt-8 pb-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center text-sm font-medium text-slate-500 dark:text-slate-400 mb-6">
            <Link to="/" className="hover:text-primary-600 dark:hover:text-primary-400">Home</Link>
            <FiChevronRight className="mx-2" />
            <Link to={`/?category=${encodeURIComponent(tool.category)}`} className="hover:text-primary-600 dark:hover:text-primary-400">
              {tool.category}
            </Link>
            <FiChevronRight className="mx-2" />
            <span className="text-slate-900 dark:text-slate-200">{tool.name}</span>
          </nav>
          
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400 rounded-2xl flex items-center justify-center flex-shrink-0">
              <SafeIcon name={tool.icon} className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-2">
                {tool.name}
              </h1>
              <p className="text-lg text-slate-600 dark:text-slate-400">
                {tool.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Tool Interface Area */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-[-2rem] relative z-10">
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl shadow-slate-200/50 dark:shadow-none border border-slate-200 dark:border-slate-800 p-6 md:p-8 min-h-[400px]">
          <Suspense fallback={
            <div className="flex flex-col items-center justify-center h-full min-h-[300px] text-slate-500">
              <FiLoader className="w-10 h-10 animate-spin text-primary-500 mb-4" />
              <p className="font-medium text-sm">Loading tool components...</p>
            </div>
          }>
            <ToolComponent />
          </Suspense>
        </div>
      </div>

      {/* SEO Content Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 prose prose-slate dark:prose-invert">
        <h2>About this {tool.name}</h2>
        <p>
          Welcome to the best free online {tool.name.toLowerCase()}. This tool runs entirely in your browser, 
          ensuring maximum privacy and blazing fast speeds. We built this utility because we believe 
          powerful digital tools should be accessible to everyone without subscriptions or data collection.
        </p>
        
        <h3>How to use</h3>
        <ol>
          <li>Upload your file or paste your input into the area above.</li>
          <li>Adjust any visible settings to fit your needs.</li>
          <li>Wait a moment for client-side processing to complete.</li>
          <li>Download or copy your results instantly!</li>
        </ol>

        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 flex gap-3 not-prose mt-8">
          <FiAlertCircle className="text-blue-500 w-6 h-6 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">Privacy First</h4>
            <p className="text-sm text-blue-800 dark:text-blue-200">Your data never leaves your device. All calculations, modifications, and processing happen directly within your browser's memory.</p>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      {tool.faqs && tool.faqs.length > 0 && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-8 border-b border-slate-200 dark:border-slate-800 pb-4">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {tool.faqs.map((faq, idx) => (
              <div key={idx} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-3">{faq.q}</h3>
                <p className="text-slate-600 dark:text-slate-400 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Related Tools */}
      {relatedTools.length > 0 && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
          <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-8 border-b border-slate-200 dark:border-slate-800 pb-4">
            More {tool.category}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {relatedTools.map(t => (
              <Link key={t.slug} to={`/tools/${t.slug}`} className="group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 hover:shadow-lg hover:border-primary-300 transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-slate-50 dark:bg-slate-800 rounded-lg text-slate-600 dark:text-slate-400 group-hover:text-primary-500 group-hover:bg-primary-50 transition-colors">
                    <SafeIcon name={t.icon} />
                  </div>
                  <h4 className="font-semibold text-slate-900 dark:text-white group-hover:text-primary-600 transition-colors">{t.name}</h4>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-2 mb-4">
                  {t.description}
                </p>
                <span className="text-xs font-semibold text-primary-600 dark:text-primary-400 flex items-center gap-1 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all">
                  Open Tool <FiArrowRight />
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};